import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { buildUserContext, type UserContext } from './userContext';
import { supabase } from './supabase';

export type UserContextApi = {
  ctx: UserContext | null;
  loading: boolean;
  refresh: () => Promise<UserContext | null>;
};

const UserCtx = createContext<UserContextApi | null>(null);

export function UserContextProvider({ children }: { children: ReactNode }) {
  const [ctx, setCtx] = useState<UserContext | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setCtx(null);
      setLoading(false);
      return null;
    }
    const freshCtx = await buildUserContext(user.id);
    setCtx(freshCtx);
    setLoading(false);
    return freshCtx;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <UserCtx.Provider value={{ ctx, refresh, loading }}>{children}</UserCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserContext(): UserContextApi {
  const v = useContext(UserCtx);
  if (!v) {
    throw new Error('useUserContext must be used within UserContextProvider');
  }
  return v;
}
